/**
 * `ProtectedRoute` — guardia di rotta che richiede una sessione autenticata.
 *
 * Design ref: "Routing e guardie per ruolo" — `ProtectedRoute` verifica la
 * sessione. Le guardie lato client sono difensive: l'autorizzazione autorevole
 * resta l'API (che nega comunque con HTTP `403`). *Validates: 4.5.*
 *
 * Comportamento:
 *  - mentre lo stato di autenticazione è in caricamento, mostra un fallback;
 *  - se non autenticato, reindirizza a `/login` (avvio flusso OIDC);
 *  - se autenticato, renderizza le rotte figlie (`<Outlet />`) o `children`.
 */
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

interface ProtectedRouteProps {
  /** Contenuto da proteggere. Se assente, usa `<Outlet />` (rotte annidate). */
  children?: ReactNode;
  /** Percorso di redirect per gli utenti non autenticati. */
  redirectTo?: string;
  /** Fallback mostrato durante il caricamento della sessione. */
  fallback?: ReactNode;
}

export function ProtectedRoute({
  children,
  redirectTo = '/login',
  fallback = <p role="status">Caricamento della sessione…</p>,
}: ProtectedRouteProps) {
  const { isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (!isAuthenticated) {
    // Conserva la destinazione per un eventuale ritorno post-login.
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return <>{children ?? <Outlet />}</>;
}
